import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import { RedirectHandler } from "@/components/RedirectHandler";

import CookieConsent from "@/components/CookieConsent";

// Core pages loaded immediately for fast initial render
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsDetail = lazy(() => import("./pages/NewsDetail"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
// PartnerProfile & ForAgenciesPage removed — content moved to valjredovisningsbyra.se / nyfikenmodellen.se (rollseparation)


const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Calculator = lazy(() => import("./pages/Calculator"));
const BusinessNetworkPage = lazy(() => import("./pages/BusinessNetworkPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const SubscriptionThankYouPage = lazy(() => import("./pages/SubscriptionThankYouPage"));



// Admin pages - lazy loaded
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminPartners = lazy(() => import("./pages/admin/Partners"));
const Testimonials = lazy(() => import("./pages/admin/Testimonials"));
const AdminNews = lazy(() => import("./pages/admin/News"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const PrivacyPages = lazy(() => import("./pages/admin/PrivacyPages"));
const SEODashboard = lazy(() => import("./pages/admin/SEODashboard"));

// Broker pages - lazy loaded
const BrokerDashboard = lazy(() => import("./pages/broker/Dashboard"));
const BrokerPartners = lazy(() => import("./pages/broker/Partners"));
const BrokerPartnerDetail = lazy(() => import("./pages/broker/PartnerDetail"));
const BrokerLeads = lazy(() => import("./pages/broker/Leads"));
const BrokerLeadDetail = lazy(() => import("./pages/broker/LeadDetail"));
const EmailSettings = lazy(() => import("./pages/broker/EmailSettings"));
const AccountSettings = lazy(() => import("./pages/broker/AccountSettings"));
const Email = lazy(() => import("./pages/broker/Email"));
const Calendar = lazy(() => import("./pages/broker/Calendar"));
const ActivityReport = lazy(() => import("./pages/broker/ActivityReport"));
const Signing = lazy(() => import("./pages/broker/Signing"));
const SigningNew = lazy(() => import("./pages/broker/SigningNew"));
const SigningDetail = lazy(() => import("./pages/broker/SigningDetail"));
const BrokerObjekt = lazy(() => import("./pages/broker/Objekt"));
const BrokerActivities = lazy(() => import("./pages/broker/Activities"));
const BrokerObjektDetail = lazy(() => import("./pages/broker/ObjektDetail"));
const BrokerObjektPrint = lazy(() => import("./pages/broker/ObjektPrint"));

const SignDocument = lazy(() => import("./pages/SignDocument"));

// Chatbot admin pages
const ChatbotFAQ = lazy(() => import("./pages/admin/ChatbotFAQ"));
const ChatbotConversations = lazy(() => import("./pages/admin/ChatbotConversations"));

// BFS public pages removed — bokföringssystem-jämförelser moved to valjredovisningsbyra.se (rollseparation)

const BidraPage = lazy(() => import("./pages/BidraPage"));
const PrisPage = lazy(() => import("./pages/PrisPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const ProspektPage = lazy(() => import("./pages/ProspektPage"));
const AlternativTillBniPage = lazy(() => import("./pages/AlternativTillBniPage"));
const DigitaltEllerFysisktPage = lazy(() => import("./pages/DigitaltEllerFysisktPage"));
const GuideForetagsnatverkPage = lazy(() => import("./pages/GuideForetagsnatverkPage"));

// BFS admin pages
const LegalPage = lazy(() => import("./pages/LegalPage"));
const AdminLegalPages = lazy(() => import("./pages/admin/LegalPages"));
const ImportNfknCrm = lazy(() => import("./pages/admin/ImportNfknCrm"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const CalendarEventPage = lazy(() => import("./pages/CalendarEventPage"));
const AdminCalendarEvents = lazy(() => import("./pages/admin/CalendarEvents"));
const ParticipantRemovePage = lazy(() => import("./pages/ParticipantRemovePage"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <RedirectHandler />
        <AuthProvider>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
          <Route path="/partners" element={<PartnersPage />} />
            {/* /partners/:slug and /for-byraer removed — redirected externally via RedirectHandler */}

            <Route path="/bidra" element={<BidraPage />} />
            <Route path="/pris" element={<PrisPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/alternativ-till-bni" element={<AlternativTillBniPage />} />
            <Route path="/digitalt-eller-fysiskt-foretagsnatverk" element={<DigitaltEllerFysisktPage />} />
            <Route path="/guide-foretagsnatverk" element={<GuideForetagsnatverkPage />} />
            <Route path="/foretagsnatverk" element={<BusinessNetworkPage />} />
            <Route path="/kalender" element={<CalendarPage />} />
            <Route path="/kalender/:slug" element={<CalendarEventPage />} />
            <Route path="/deltagarlista/avboka" element={<ParticipantRemovePage />} />

            
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/integritetsskydd-cookies-och-gdpr" element={<PrivacyPolicy />} />
            
            {/* Broker Routes */}
            <Route 
              path="/broker" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <BrokerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/partners" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <BrokerPartners />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/partners/:id" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <BrokerPartnerDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/leads"
              element={
                <ProtectedRoute requireBrokerOrAdmin orBrokerage>
                  <BrokerLeads />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/leads/:id" 
              element={
                <ProtectedRoute requireBrokerOrAdmin orBrokerage>
                  <BrokerLeadDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/email" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <Email />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/calendar" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <Calendar />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/broker/aktiviteter"
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <BrokerActivities />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/broker/activity-report" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <ActivityReport />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/broker/objekt/:id/utskrift"
              element={
                <ProtectedRoute requireBrokerage>
                  <BrokerObjektPrint />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/objekt/:id"
              element={
                <ProtectedRoute requireBrokerage>
                  <BrokerObjektDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/objekt"
              element={
                <ProtectedRoute requireBrokerage>
                  <BrokerObjekt />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/broker/kalkylator" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <Calculator />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/objekt"
              element={
                <ProtectedRoute requireAdmin>
                  <BrokerObjekt />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/broker/signing" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <Signing />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/signing/new" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <SigningNew />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/signing/:id" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <SigningDetail />
                </ProtectedRoute>
              } 
            />
            <Route path="/sign" element={<SignDocument />} />
            <Route path="/prospekt" element={<ProspektPage />} />
            <Route 
              path="/broker/email-settings"
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <EmailSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broker/account-settings" 
              element={
                <ProtectedRoute requireBrokerOrAdmin>
                  <AccountSettings />
                </ProtectedRoute>
              } 
            />
            
            {/* Företagsmäkleri (separate permission, not role "mäklare") */}



            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/partners" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPartners />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/recensioner"
              element={
                <ProtectedRoute requireAdmin>
                  <Testimonials lockedKategori="natverket" />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/news"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminNews />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminUsers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/privacy-pages" 
              element={
                <ProtectedRoute requireAdmin>
                  <PrivacyPages />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/seo" 
              element={
                <ProtectedRoute requireAdmin>
                  <SEODashboard />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/admin/chatbot-faq" 
              element={
                <ProtectedRoute requireAdmin>
                  <ChatbotFAQ />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/chatbot-conversations" 
              element={
                <ProtectedRoute requireAdmin>
                  <ChatbotConversations />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/calendar" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCalendarEvents />
                </ProtectedRoute>
              } 
            />
            
            {/* BFS public routes removed — redirected to valjredovisningsbyra.se via RedirectHandler */}

            {/* BFS Admin Routes */}
            <Route path="/admin/legal-pages" element={<ProtectedRoute requireAdmin><AdminLegalPages /></ProtectedRoute>} />
            <Route path="/admin/import-nfkn-crm" element={<ProtectedRoute requireAdmin><ImportNfknCrm /></ProtectedRoute>} />

            {/* Legal Pages */}
            <Route path="/byravillkor" element={<LegalPage />} />
            <Route path="/tillvaxtpartner-villkor" element={<LegalPage />} />

            {/* Public Landing Pages */}
            <Route path="/om-oss" element={<AboutPage />} />
            <Route path="/tack" element={<ThankYouPage />} />
            <Route path="/tack-prenumeration" element={<SubscriptionThankYouPage />} />

            {/* /behovsguiden and /vanliga-fragor removed — redirected externally via RedirectHandler */}
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;